import React from 'react';
import { AlertTriangle, Clock, Wifi, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { useSystemStatus } from '../api/system-configuration/use-system-configuration';

const MaintenancePage: React.FC = () => {
    const { t } = useTranslation('maintenance');
    const { data: systemStatus, isLoading, refreshSystemStatus } = useSystemStatus();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">{t('checkingStatus')}</p>
                </div>
            </div>
        );
    }

    const isMaintenanceMode = systemStatus?.maintenance_mode === true;
    const maintenanceMessage = systemStatus?.maintenance_message || t('maintenanceDefaultMessage');

    if (!isMaintenanceMode) {
        return null; // Don't show maintenance page if not in maintenance mode
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="max-w-md mx-auto text-center p-8">
                {/* Maintenance Icon */}
                <div className="mb-6">
                    <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-foreground mb-2">
                    {t('systemMaintenance')}
                </h1>

                {/* Message */}
                <p className="text-muted-foreground mb-6 leading-relaxed">{maintenanceMessage}</p>

                {/* Status Indicators */}
                <div className="space-y-3 mb-8">
                    <div className="flex items-center justify-center space-x-2 text-sm">
                        <WifiOff className="h-4 w-4 text-red-500" />
                        <span className="text-muted-foreground">
                            {t('tradingTemporarilyUnavailable')}
                        </span>
                    </div>

                    {systemStatus?.trading_enabled === false && (
                        <div className="flex items-center justify-center space-x-2 text-sm">
                            <Clock className="h-4 w-4 text-orange-500" />
                            <span className="text-muted-foreground">{t('tradingDisabled')}</span>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    <Button
                        onClick={refreshSystemStatus}
                        variant="outline"
                        className="w-full"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                {t('checking')}
                            </>
                        ) : (
                            <>
                                <Wifi className="h-4 w-4 mr-2" />
                                {t('checkAgain')}
                            </>
                        )}
                    </Button>

                    <p className="text-xs text-muted-foreground">{t('notifyWhenBack')}</p>
                </div>

                {/* Contact Information */}
                {systemStatus?.support_email && (
                    <div className="mt-8 pt-6 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">{t('needAssistance')}</p>
                        <a
                            href={`mailto:${systemStatus.support_email}`}
                            className="text-xs text-primary hover:underline"
                        >
                            {t('contactSupport')}
                        </a>
                        {systemStatus?.support_phone && (
                            <>
                                <span className="text-xs text-muted-foreground mx-2">•</span>
                                <a
                                    href={`tel:${systemStatus.support_phone}`}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {systemStatus.support_phone}
                                </a>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MaintenancePage;
